/**
 * County / state pairs from Mapbox Search Geocoding v6 reverse (used for pipeline tap jurisdiction gate).
 */

export type CountyStatePair = { county: string; state: string };

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

/**
 * Parse `FeatureCollection` JSON from
 * `GET https://api.mapbox.com/search/geocode/v6/reverse?...`
 */
export function parseCountyStatePairsFromMapboxSearchReverseV6(json: unknown): CountyStatePair[] {
  if (!json || typeof json !== "object") return [];
  const root = json as Record<string, unknown>;
  if (root.type !== "FeatureCollection" || !Array.isArray(root.features)) return [];

  const out: CountyStatePair[] = [];
  const seen = new Set<string>();

  for (const feat of root.features) {
    const fr = asRecord(feat);
    const props = asRecord(fr?.properties);
    const ctx = asRecord(props?.context);
    const district = asRecord(ctx?.district);
    const region = asRecord(ctx?.region);
    const rawCounty = district?.name;
    const rawState = region?.name;
    if (typeof rawCounty !== "string" || typeof rawState !== "string") continue;
    const county = rawCounty.trim();
    const state = rawState.trim();
    if (!county || !state) continue;
    const k = `${county.toLowerCase()}|${state.toLowerCase()}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ county, state });
  }

  return out;
}

export async function fetchCountyStatePairsReverseMapboxLngLat(params: {
  longitude: number;
  latitude: number;
  accessToken: string;
  signal?: AbortSignal;
}): Promise<CountyStatePair[]> {
  const { longitude, latitude, accessToken, signal } = params;
  const url =
    `https://api.mapbox.com/search/geocode/v6/reverse` +
    `?longitude=${encodeURIComponent(String(longitude))}` +
    `&latitude=${encodeURIComponent(String(latitude))}` +
    `&access_token=${encodeURIComponent(accessToken)}`;

  const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal });
  if (!res.ok) return [];
  const json: unknown = await res.json().catch(() => null);
  return parseCountyStatePairsFromMapboxSearchReverseV6(json);
}
